import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

interface Campanha {
  id: string;
  nome: string;
  mensagem: string;
  status: 'agendada' | 'em_andamento' | 'concluida' | 'cancelada';
  dataAgendamento: Date;
  segmentos: string[];
  estatisticas: {
    total: number;
    enviadas: number;
    entregues: number;
    lidas: number;
    respondidas: number;
  };
}

const Campanhas: React.FC = () => {
  const [campanhas, setCampanhas] = useState<Campanha[]>([]);
  const [filteredCampanhas, setFilteredCampanhas] = useState<Campanha[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [novaCampanha, setNovaCampanha] = useState<{ nome: string; mensagem: string; dataAgendamento: string; segmentos: string[] }>({
    nome: '',
    mensagem: '',
    dataAgendamento: new Date().toISOString().slice(0, 16), // Ajustado para formato datetime-local
    segmentos: []
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Segmentos disponíveis (em um sistema real, viriam da API)
  const segmentosDisponiveis = [
    { id: '1', nome: 'Clientes Novos' },
    { id: '2', nome: 'Clientes Inativos (30+ dias)' },
    { id: '3', nome: 'Compradores Frequentes' },
    { id: '4', nome: 'Aniversariantes do Mês' },
    { id: '5', nome: 'Região Sul' },
    { id: '6', nome: 'Região Sudeste' },
    { id: '7', nome: 'Interessados em Produto X' }
  ];

  // Dados de exemplo
  useEffect(() => {
    const exemploCampanhas: Campanha[] = [
      {
        id: '1',
        nome: 'Promoção de Verão',
        mensagem: 'Olá! Aproveite nossa promoção de verão com até 50% de desconto em produtos selecionados. Acesse nossa loja: https://exemplo.com/promocao',
        status: 'concluida',
        dataAgendamento: new Date('2025-01-15T10:00:00'),
        segmentos: ['Clientes Inativos (30+ dias)', 'Região Sudeste'],
        estatisticas: {
          total: 1500,
          enviadas: 1500,
          entregues: 1450,
          lidas: 1200,
          respondidas: 350
        }
      },
      {
        id: '2',
        nome: 'Lançamento Produto Novo',
        mensagem: 'Temos novidades! Acabamos de lançar nosso novo produto X que vai revolucionar sua experiência. Saiba mais: https://exemplo.com/lancamento',
        status: 'em_andamento',
        dataAgendamento: new Date('2025-05-10T14:30:00'),
        segmentos: ['Compradores Frequentes', 'Interessados em Produto X'],
        estatisticas: {
          total: 800,
          enviadas: 450,
          entregues: 440,
          lidas: 300,
          respondidas: 120
        }
      },
      {
        id: '3',
        nome: 'Pesquisa de Satisfação',
        mensagem: 'Olá! Sua opinião é muito importante para nós. Poderia responder nossa pesquisa de satisfação? Leva apenas 2 minutos: https://exemplo.com/pesquisa',
        status: 'agendada',
        dataAgendamento: new Date('2025-06-01T09:00:00'),
        segmentos: ['Clientes Novos', 'Compradores Frequentes'],
        estatisticas: {
          total: 2000,
          enviadas: 0,
          entregues: 0,
          lidas: 0,
          respondidas: 0
        }
      },
      {
        id: '4',
        nome: 'Aniversário da Loja',
        mensagem: 'Estamos comemorando 5 anos! Para agradecer sua confiança, preparamos um cupom especial de 20% OFF: ANIVERSARIO5. Válido até o fim do mês.',
        status: 'cancelada',
        dataAgendamento: new Date('2025-04-20T12:00:00'),
        segmentos: ['Clientes Novos', 'Clientes Inativos (30+ dias)', 'Compradores Frequentes'],
        estatisticas: {
          total: 3000,
          enviadas: 0,
          entregues: 0,
          lidas: 0,
          respondidas: 0
        }
      }
    ];

    setCampanhas(exemploCampanhas);
    setFilteredCampanhas(exemploCampanhas);
  }, []);

  // Filtrar campanhas com base na pesquisa e na aba ativa
  useEffect(() => {
    let filtered = campanhas;

    // Filtrar por status
    if (activeTab !== 'todas') {
      filtered = filtered.filter((campanha) => campanha.status === activeTab);
    }

    // Filtrar por termo de pesquisa
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (campanha) =>
          campanha.nome.toLowerCase().includes(term) ||
          campanha.mensagem.toLowerCase().includes(term) ||
          campanha.segmentos.some(seg => seg.toLowerCase().includes(term))
      );
    }

    // Ordenar por data de agendamento (mais recente primeiro)
    filtered = [...filtered].sort(
      (a, b) => new Date(b.dataAgendamento).getTime() - new Date(a.dataAgendamento).getTime()
    );

    setFilteredCampanhas(filtered);
  }, [campanhas, searchTerm, activeTab]);

  // Adicionar nova campanha
  const handleAddCampanha = () => {
    const newId = (campanhas.length + 1).toString();
    
    const novaCampanhaObj: Campanha = {
      id: newId,
      nome: novaCampanha.nome,
      mensagem: novaCampanha.mensagem,
      status: 'agendada',
      dataAgendamento: new Date(novaCampanha.dataAgendamento),
      segmentos: novaCampanha.segmentos,
      estatisticas: {
        total: Math.floor(Math.random() * 2000) + 500, // Número aleatório para exemplo
        enviadas: 0,
        entregues: 0,
        lidas: 0,
        respondidas: 0
      }
    };
    
    setCampanhas([...campanhas, novaCampanhaObj]);
    setNovaCampanha({
      nome: '',
      mensagem: '',
      dataAgendamento: new Date().toISOString().split('T')[0],
      segmentos: []
    });
    setIsDialogOpen(false);
  };

  // Cancelar campanha
  const handleCancelCampanha = (id: string) => {
    setCampanhas(campanhas.map(campanha => 
      campanha.id === id ? {...campanha, status: 'cancelada'} : campanha
    ));
  };

  // Formatar data
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obter cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agendada':
        return 'bg-blue-100 text-blue-800';
      case 'em_andamento':
        return 'bg-yellow-100 text-yellow-800';
      case 'concluida':
        return 'bg-green-100 text-green-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obter texto do status
  const getStatusText = (status: string) => {
    switch (status) {
      case 'agendada':
        return 'Agendada';
      case 'em_andamento':
        return 'Em andamento';
      case 'concluida':
        return 'Concluída';
      case 'cancelada':
        return 'Cancelada';
      default:
        return status;
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Campanhas de Mensagens</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Nova Campanha
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Criar Nova Campanha</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Campanha</Label>
                <Input
                  id="nome"
                  value={novaCampanha.nome}
                  onChange={(e) => setNovaCampanha({...novaCampanha, nome: e.target.value})}
                  placeholder="Ex: Promoção de Verão"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mensagem">Mensagem</Label>
                <Textarea
                  id="mensagem"
                  value={novaCampanha.mensagem}
                  onChange={(e) => setNovaCampanha({...novaCampanha, mensagem: e.target.value})}
                  placeholder="Digite a mensagem que será enviada..."
                  rows={4}
                />
                <p className="text-xs text-gray-500">
                  Dica: Personalize sua mensagem com variáveis como {'{nome}'}, {'{empresa}'}, etc.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dataAgendamento">Data de Envio</Label>
                <Input
                  id="dataAgendamento"
                  type="datetime-local"
                  value={novaCampanha.dataAgendamento}
                  onChange={(e) => setNovaCampanha({...novaCampanha, dataAgendamento: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="segmentos">Segmentos</Label>
                <Select
                  onValueChange={(value) => {
                    const segmento = segmentosDisponiveis.find(s => s.id === value)?.nome || '';
                    if (segmento && !novaCampanha.segmentos.includes(segmento)) {
                      setNovaCampanha({
                        ...novaCampanha, 
                        segmentos: [...novaCampanha.segmentos, segmento]
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione os segmentos" />
                  </SelectTrigger>
                  <SelectContent>
                    {segmentosDisponiveis.map((segmento) => (
                      <SelectItem key={segmento.id} value={segmento.id}>
                        {segmento.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex flex-wrap gap-2 mt-2">
                  {novaCampanha.segmentos.map((segmento, index) => (
                    <div key={index} className="bg-gray-100 px-2 py-1 rounded-full flex items-center text-sm">
                      {segmento}
                      <button
                        className="ml-1 text-gray-500 hover:text-red-500"
                        onClick={() => {
                          setNovaCampanha({
                            ...novaCampanha,
                            segmentos: novaCampanha.segmentos.filter((_, i) => i !== index)
                          });
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button 
                className="bg-green-600 hover:bg-green-700" 
                onClick={handleAddCampanha}
                disabled={!novaCampanha.nome || !novaCampanha.mensagem || novaCampanha.segmentos.length === 0}
              >
                Criar Campanha
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <Input
          type="text"
          placeholder="Pesquisar campanhas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      <Tabs defaultValue="todas" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="todas">Todas</TabsTrigger>
          <TabsTrigger value="agendada">Agendadas</TabsTrigger>
          <TabsTrigger value="em_andamento">Em Andamento</TabsTrigger>
          <TabsTrigger value="concluida">Concluídas</TabsTrigger>
          <TabsTrigger value="cancelada">Canceladas</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredCampanhas.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhuma campanha encontrada.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredCampanhas.map((campanha) => (
                <Card key={campanha.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{campanha.nome}</CardTitle>
                        <div className="flex items-center mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(campanha.status)}`}>
                            {getStatusText(campanha.status)}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            Agendada para: {formatDate(campanha.dataAgendamento)}
                          </span>
                        </div>
                      </div>
                      {campanha.status === 'agendada' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-200 hover:bg-red-50"
                          onClick={() => handleCancelCampanha(campanha.id)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Mensagem:</h3>
                      <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded border">
                        {campanha.mensagem}
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-500 mb-1">Segmentos:</h3>
                      <div className="flex flex-wrap gap-1">
                        {campanha.segmentos.map((segmento, index) => (
                          <span key={index} className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
                            {segmento}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Estatísticas:</h3>
                      <div className="grid grid-cols-5 gap-2 text-center">
                        <div className="bg-gray-50 p-2 rounded">
                          <div className="text-lg font-semibold">{campanha.estatisticas.total}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <div className="text-lg font-semibold text-blue-700">
                            {campanha.estatisticas.enviadas}
                          </div>
                          <div className="text-xs text-blue-700">Enviadas</div>
                        </div>
                        <div className="bg-green-50 p-2 rounded">
                          <div className="text-lg font-semibold text-green-700">
                            {campanha.estatisticas.entregues}
                          </div>
                          <div className="text-xs text-green-700">Entregues</div>
                        </div>
                        <div className="bg-yellow-50 p-2 rounded">
                          <div className="text-lg font-semibold text-yellow-700">
                            {campanha.estatisticas.lidas}
                          </div>
                          <div className="text-xs text-yellow-700">Lidas</div>
                        </div>
                        <div className="bg-purple-50 p-2 rounded">
                          <div className="text-lg font-semibold text-purple-700">
                            {campanha.estatisticas.respondidas}
                          </div>
                          <div className="text-xs text-purple-700">Respondidas</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Campanhas;
